/**
 * Scorecard — horizontal-bar leaderboard primitive used by the
 * Events → Organizers module (and re-usable wherever a ranked list of
 * actors-with-a-headline-metric needs to be visualised).
 *
 * Each row renders, from the LOGICAL start (right in RTL) to the
 * LOGICAL end (left in RTL):
 *   [rank pill] [label + sub]  ........  [bar track + fill]  [primary value]  [rate chip]  [events chip]
 *
 * Bars use the same .cellbar visual vocabulary as the funnel district
 * table (.track + .fill, accent fill when present), so no new CSS is
 * required.
 */
import { formatNumber } from '../../utils/format';

export interface ScorecardRow {
  /** Stable key for React + click handlers. */
  id: string;
  /** Primary display name. The Organizers view falls back to the
   *  Airtable record id here when the API has no resolved name. */
  label: string;
  /** Secondary context line (e.g. district, format mix). Optional. */
  sub?: string;
  /** Headline metric for the bar (e.g. attended count). */
  primary: number;
  /** Secondary count (e.g. events organised). Rendered as a quiet chip. */
  secondary?: number;
  /** 0..1 rate (e.g. yes_show). Rendered as a quiet chip. */
  rate?: number;
  /** When true the row gets a muted "below threshold" treatment and
   *  no bar fill — rate/secondary are still allowed for context. */
  insufficient?: boolean;
}

/**
 * Column-header labels for the optional header row. Pass `columns` to
 * render a tiny header row inside the `<ol>` that mirrors the grid
 * template so headers line up with their data columns.
 *
 * Each entry maps to the corresponding logical column in the row:
 *   rank | label (+ sub) | bar | primary | rate | secondary
 *
 * Passing `undefined` or empty string for an entry leaves that column
 * blank. The bar column is typically labelled "תרשים השוואה" but the
 * "below threshold" panel uses "אין דירוג" to be explicit that no
 * comparison bar is rendered for those rows.
 */
export interface ScorecardColumns {
  rank?: string;
  label?: string;
  bar?: string;
  primary?: string;
  rate?: string;
  secondary?: string;
}

export interface ScorecardProps {
  rows: ScorecardRow[];
  /** Override the bar-scaling denominator. Defaults to max(primary). */
  maxPrimary?: number;
  /** Caption shown next to secondary counts ("אירועים"). */
  secondaryLabel?: string;
  /** Caption shown next to the rate chip ("המרה"). */
  rateLabel?: string;
  /** When true, rows are numbered. Default: true. Pass false for the
   *  "below threshold" panel where rank is meaningless. */
  showRank?: boolean;
  /** Optional column-header labels. When provided, a small header row
   *  is rendered above the rows using the same grid template, so users
   *  can read what each column means. */
  columns?: ScorecardColumns;
  /** Optional renderer for trailing per-row decoration (e.g. an
   *  `<InsufficientData/>` chip). Receives the original row. */
  renderTrailing?: (row: ScorecardRow) => React.ReactNode;
}

export function Scorecard({
  rows,
  maxPrimary,
  secondaryLabel = 'אירועים',
  rateLabel = 'המרה',
  showRank = true,
  columns,
  renderTrailing,
}: ScorecardProps) {
  const denom = Math.max(
    1,
    maxPrimary ?? rows.reduce((m, r) => Math.max(m, r.primary), 0),
  );

  return (
    <ol className="scorecard">
      {columns && (
        <li className="scorecard-header" aria-hidden>
          {showRank && <span className="scorecard-rank">{columns.rank ?? ''}</span>}
          <div className="scorecard-label">{columns.label ?? ''}</div>
          <div className="scorecard-bar">{columns.bar ?? ''}</div>
          <span className="scorecard-primary">{columns.primary ?? ''}</span>
          <span className="scorecard-chip-head">{columns.rate ?? ''}</span>
          <span className="scorecard-chip-head">{columns.secondary ?? ''}</span>
          {renderTrailing && <span className="scorecard-trailing" />}
        </li>
      )}
      {rows.map((r, idx) => {
        const share = r.insufficient ? 0 : r.primary / denom;
        const cls = ['scorecard-row', r.insufficient && 'is-insufficient']
          .filter(Boolean)
          .join(' ');
        return (
          <li key={r.id} className={cls}>
            {showRank && (
              <span className="scorecard-rank" aria-hidden>
                {r.insufficient ? '—' : idx + 1}
              </span>
            )}
            <div className="scorecard-label">
              <div className="scorecard-name">{r.label}</div>
              {r.sub && <div className="scorecard-sub">{r.sub}</div>}
            </div>
            <div className="scorecard-bar">
              <div className="cellbar">
                <div className="track">
                  <div
                    className={
                      'fill' + (r.insufficient ? '' : ' accent')
                    }
                    style={{ width: `${Math.round(share * 100)}%` }}
                  />
                </div>
              </div>
            </div>
            <span className="scorecard-primary">
              {formatNumber(r.primary)}
            </span>
            {typeof r.rate === 'number' && (
              <span
                className="scorecard-chip"
                title={`${rateLabel}: ${(r.rate * 100).toFixed(1)}%`}
              >
                {r.insufficient ? '—' : `${Math.round(r.rate * 100)}%`}
              </span>
            )}
            {typeof r.secondary === 'number' && (
              <span
                className="scorecard-chip scorecard-chip-quiet"
                title={`${secondaryLabel}: ${formatNumber(r.secondary)}`}
              >
                {formatNumber(r.secondary)} {secondaryLabel}
              </span>
            )}
            {renderTrailing && (
              <span className="scorecard-trailing">{renderTrailing(r)}</span>
            )}
          </li>
        );
      })}

      {/* All styles colocated so the primitive is drop-in without
          requiring an edit to dashboard.css. Mirrors the .cellbar
          visual vocabulary used by the funnel district table. */}
      <style>{`
        .scorecard {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
        }
        .scorecard-row {
          display: grid;
          grid-template-columns: auto minmax(180px, 1.4fr) minmax(160px, 2fr) auto auto auto auto;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          border-bottom: 1px solid var(--hairline);
          font-family: var(--font-sans);
          font-size: 13px;
          color: var(--ink);
        }
        .scorecard-row:last-child { border-bottom: none; }
        .scorecard-header {
          display: grid;
          grid-template-columns: auto minmax(180px, 1.4fr) minmax(160px, 2fr) auto auto auto auto;
          align-items: center;
          gap: 12px;
          padding: 8px 14px;
          border-bottom: 1px solid var(--ink-3, currentColor);
          font-family: var(--font-sans);
          font-size: 10.5px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--ink-3);
          font-weight: 600;
          text-align: start;
        }
        .scorecard-header .scorecard-primary,
        .scorecard-header .scorecard-chip-head {
          font-family: var(--font-sans);
          font-weight: 600;
          font-size: 10.5px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--ink-3);
          white-space: nowrap;
          text-align: end;
        }
        .scorecard-header .scorecard-chip-head {
          text-align: center;
        }
        .scorecard-row.is-insufficient {
          color: var(--ink-3);
          background: var(--paper-2, #faf8f3);
        }
        .scorecard-rank {
          font-family: var(--font-mono);
          font-size: 11px;
          color: var(--ink-3);
          min-width: 24px;
          text-align: center;
        }
        .scorecard-label {
          min-width: 0;
        }
        .scorecard-name {
          font-weight: 600;
          color: inherit;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .scorecard-sub {
          font-size: 11px;
          color: var(--ink-3);
          margin-top: 2px;
        }
        .scorecard-bar { min-width: 0; }
        .scorecard-primary {
          font-family: var(--font-mono);
          font-variant-numeric: tabular-nums;
          font-size: 13px;
          font-weight: 600;
          min-width: 56px;
          text-align: end;
        }
        .scorecard-chip {
          font-family: var(--font-mono);
          font-size: 11px;
          padding: 2px 8px;
          border: 1px solid var(--hairline);
          border-radius: 999px;
          color: var(--ink-2);
          background: var(--paper);
          white-space: nowrap;
        }
        .scorecard-chip-quiet {
          color: var(--ink-3);
          border-color: transparent;
          background: transparent;
        }
        .scorecard-trailing {
          display: inline-flex;
          align-items: center;
        }
        @media (max-width: 720px) {
          .scorecard-header { display: none; }
          .scorecard-row {
            grid-template-columns: auto 1fr auto;
            grid-template-areas:
              "rank label primary"
              ".    bar   bar"
              ".    chips chips";
            row-gap: 6px;
          }
          .scorecard-rank { grid-area: rank; }
          .scorecard-label { grid-area: label; }
          .scorecard-bar { grid-area: bar; }
          .scorecard-primary { grid-area: primary; }
          .scorecard-chip,
          .scorecard-chip-quiet,
          .scorecard-trailing {
            grid-area: chips;
          }
        }
      `}</style>
    </ol>
  );
}
